import { takeLatest, call, put, all } from 'redux-saga/effects';
import {
  loadServices,
  loadServicesSuccess,
  loadServicesError,
  saveServiceRequest,
  savePrintMaterialRequest,
  removePrintMaterialRequest,
  adjustMaterialStockRequest,
} from './actions';
import {
  listServices,
  createService,
  updateService,
  deleteService,
  listPrintMaterials,
  createPrintMaterial,
  updatePrintMaterial,
  deletePrintMaterial,
  adjustMaterialStock,
} from '@/services/services';
import { getActiveShopId } from '@/utils/shopSession';
import { hydrateServices, hydratePrintMaterials } from '@/containers/posData/actions';
import type { Service, PrintMaterial } from '@/lib/types';
import type { ServiceInput, PrintMaterialInput } from '@/services/services';

function mapService(dto: any): Service {
  // Rates/bom come back with numeric materialIds (jsonb) while catalog ids
  // are strings client-side — normalize once so every === lookup matches.
  const strRate = (r: any) => ({ ...r, materialId: String(r.materialId) });
  return {
    id: String(dto.id),
    name: dto.name,
    category: dto.category || undefined,
    kind: (dto.kind as Service['kind']) || 'standard',
    sellingPrice: Number(dto.sellingPrice),
    dealerPrice: dto.dealerPrice != null ? Number(dto.dealerPrice) : undefined,
    isCommissioned: dto.isCommissioned === true,
    defaultCommission: Number(dto.defaultCommission ?? 0),
    modes: Array.isArray(dto.modes) ? dto.modes : [],
    laborCharge: Number(dto.laborCharge ?? 0),
    rates: (dto.rates || []).map(strRate),
    bom: (dto.bom || []).map(strRate),
    fulfillment: dto.fulfillment || 'counter',
    team: dto.team ?? null,
    outsourced: dto.outsourced === true,
    vendorId: dto.vendorId != null ? String(dto.vendorId) : null,
    outsideCost: Number(dto.outsideCost ?? 0),
  };
}

function mapMaterial(dto: any): PrintMaterial {
  return {
    id: String(dto.id),
    name: dto.name,
    unit: dto.unit,
    costPerUnit: Number(dto.costPerUnit),
    sellingPrice: Number(dto.sellingPrice ?? 0),
    stockQuantity: Number(dto.stockQuantity ?? 0),
    lowStockThreshold: Number(dto.lowStockThreshold ?? 0),
    category: dto.category ?? 'general',
  };
}

function serviceInput(service: Service): ServiceInput {
  return {
    name: service.name,
    category: service.category,
    kind: service.kind,
    sellingPrice: service.sellingPrice,
    dealerPrice: service.dealerPrice,
    isCommissioned: service.isCommissioned,
    defaultCommission: service.defaultCommission,
    modes: service.modes,
    rates: service.rates,
    bom: service.bom,
    laborCharge: service.laborCharge,
    fulfillment: service.fulfillment,
    team: service.team,
    outsourced: service.outsourced,
    vendorId: service.vendorId,
    outsideCost: service.outsideCost,
  };
}

function materialInput(material: PrintMaterial): PrintMaterialInput {
  return {
    name: material.name,
    unit: material.unit,
    costPerUnit: material.costPerUnit,
    sellingPrice: material.sellingPrice,
    stockQuantity: material.stockQuantity,
    lowStockThreshold: material.lowStockThreshold,
    category: material.category,
  };
}

export function* handleLoadServices(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadServicesError('No active shop selected'));
      return;
    }
    const [rawServices, rawMaterials]: [any[], any[]] = yield all([
      call(listServices, shopId),
      call(listPrintMaterials, shopId),
    ]);
    const services = rawServices.map(mapService);
    const materials = rawMaterials.map(mapMaterial);
    yield put(hydrateServices(services));
    yield put(hydratePrintMaterials(materials));
    yield put(loadServicesSuccess(services, materials));
  } catch (e: any) {
    yield put(loadServicesError(e?.message || 'Failed to load services'));
  }
}

export function* handleSaveService(
  action: ReturnType<typeof saveServiceRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { service, isNew } = action.payload;
    if (isNew) {
      yield call(createService, shopId, serviceInput(service));
    } else {
      yield call(updateService, shopId, Number(service.id), serviceInput(service));
    }
    yield put(loadServices());
  } catch (e: any) {
    yield put(loadServicesError(e?.message || 'Failed to save service'));
  }
}

export function* handleSavePrintMaterial(
  action: ReturnType<typeof savePrintMaterialRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { material, isNew } = action.payload;
    if (isNew) {
      yield call(createPrintMaterial, shopId, materialInput(material));
    } else {
      yield call(
        updatePrintMaterial,
        shopId,
        Number(material.id),
        materialInput(material)
      );
    }
    yield put(loadServices());
  } catch (e: any) {
    yield put(loadServicesError(e?.message || 'Failed to save material'));
  }
}

export function* handleRemovePrintMaterial(
  action: ReturnType<typeof removePrintMaterialRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield call(deletePrintMaterial, shopId, Number(action.payload.id));
    yield put(loadServices());
  } catch (e: any) {
    yield put(loadServicesError(e?.message || 'Failed to remove material'));
  }
}

export function* handleAdjustMaterialStock(
  action: ReturnType<typeof adjustMaterialStockRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { id, change, reason } = action.payload;
    yield call(adjustMaterialStock, shopId, Number(id), { change, reason });
    yield put(loadServices());
  } catch (e: any) {
    yield put(loadServicesError(e?.message || 'Failed to adjust stock'));
  }
}

export default function* saga() {
  yield takeLatest(loadServices.type, handleLoadServices);
  yield takeLatest(saveServiceRequest.type, handleSaveService);
  yield takeLatest(savePrintMaterialRequest.type, handleSavePrintMaterial);
  yield takeLatest(removePrintMaterialRequest.type, handleRemovePrintMaterial);
  yield takeLatest(adjustMaterialStockRequest.type, handleAdjustMaterialStock);
}
